import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

export const userLocationQueryKey = ['surfaces', 'trust', 'location'] as const;

// Mirrors GET/POST /api/surfaces/trust/location (Next + Supabase, trust_settings.location_* columns).
// The saved city the daily brief's weather tile forecasts against; null when the user hasn't set one
// (the tile then falls back to the Google-calendar-timezone proxy).
export interface UserLocation {
  label: string | null;
  lat: number;
  lon: number;
  timezone: string | null;
}

export interface LocationResponse {
  location: UserLocation | null;
}

export function useUserLocation() {
  const query = useQuery({
    queryKey: userLocationQueryKey,
    queryFn: () => apiFetch<LocationResponse>('/api/surfaces/trust/location'),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  return { ...query, data: query.data ?? undefined };
}

// Saves the user's location (an empty query clears it). The POST geocodes the typed city
// server-side; on success it invalidates the location read AND the brief so the masthead weather
// tile refetches with the new city. Throws an ApiError with status 422 when the city doesn't
// resolve — the caller distinguishes that from a real failure.
export function useSaveLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (query: string) =>
      apiFetch<LocationResponse>('/api/surfaces/trust/location', {
        method: 'POST',
        body: JSON.stringify({ query }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userLocationQueryKey });
      queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-today'] });
    },
  });
}
